import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table as STable, Loader, Message, Pagination, Dropdown, Grid, GridColumn, Icon } from 'semantic-ui-react';
import { getIngestionLog, getIngestionLogDocumentUrl, IngestionLogEntry } from '~/api/admin';
import '~/features/admin/companies/styles.scss';

const PAGE_SIZE = 50;
const colStyle = { padding: '1rem 0 0 1rem' };
const controlFontSize = { fontSize: '.9rem' };

const TYPE_OPTIONS = [
  { key: 'document_ingestion', value: 'document_ingestion', text: 'Document ingestion (project)' },
  { key: 'create_from_document', value: 'create_from_document', text: 'Create from document' },
];

const STATUS_OPTIONS = [
  { key: 'PENDING', value: 'PENDING', text: 'Pending' },
  { key: 'SUCCESS', value: 'SUCCESS', text: 'Success' },
  { key: 'ERROR', value: 'ERROR', text: 'Error' },
];

function statusColor(status: string): string {
  if (status === 'SUCCESS') return '#1a7f3c';
  if (status === 'ERROR') return '#b00020';
  if (status === 'PENDING') return '#7a6800';
  return '#444';
}

function DocumentDownloadLink({ apiEventId }: { apiEventId: number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    try {
      setLoading(true);
      setError(null);
      const { url } = await getIngestionLogDocumentUrl(apiEventId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
      <button
        type='button'
        onClick={handleClick}
        disabled={loading}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: loading ? 'default' : 'pointer',
          color: '#1a73e8',
          fontSize: '0.85rem',
          textDecoration: 'underline',
        }}
      >
        <Icon name='download' />
        {loading ? 'Opening…' : 'Download'}
      </button>
      {error && <span style={{ color: '#b00020', fontSize: '0.75rem' }}>{error}</span>}
    </span>
  );
}

function resultSummary(entry: IngestionLogEntry): string {
  const result = entry.result as any;
  if (result == null) return '';
  if (entry.status === 'ERROR') {
    return typeof result?.message === 'string' ? result.message : 'Error';
  }
  if (typeof result?.notes?.[0] === 'string') return result.notes[0];
  if (Array.isArray(result?.updatedFields) && result.updatedFields.length) {
    return `Updated: ${result.updatedFields.slice(0, 4).join(', ')}${result.updatedFields.length > 4 ? '…' : ''}`;
  }
  return 'View JSON';
}

export default function IngestionLogTable() {
  const [entries, setEntries] = useState<IngestionLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Ingestion Log | Conductor Solar';
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const offset = (currentPage - 1) * PAGE_SIZE;
        const params: any = { limit: PAGE_SIZE, offset, sortDirection };
        if (typeFilter) params.type = typeFilter;
        if (statusFilter) params.status = statusFilter;
        const response = await getIngestionLog(params);
        setEntries(response.entries || []);
        setTotal(response.entries.length || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to fetch ingestion log');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentPage, sortDirection, typeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasFilters = useMemo(
    () => Boolean(typeFilter) || Boolean(statusFilter),
    [typeFilter, statusFilter]
  );

  const clearAllFilters = () => {
    setTypeFilter(undefined);
    setStatusFilter(undefined);
    setCurrentPage(1);
  };

  const handleToggleSortByDate = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  const sortIcon = sortDirection === 'asc' ? ' ▲' : ' ▼';

  return (
    <div>
      <div className='card'>
        <div style={{ margin: '1rem 0 2rem', paddingRight: '1rem' }}>
          <Grid stackable>
            <GridColumn mobile={16} tablet={6} computer={4} style={colStyle}>
              <Dropdown
                placeholder='All types...'
                fluid
                selection
                clearable
                options={TYPE_OPTIONS}
                value={typeFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setTypeFilter((value as string) || undefined);
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>
            <GridColumn mobile={16} tablet={6} computer={4} style={colStyle}>
              <Dropdown
                placeholder='All statuses...'
                fluid
                selection
                clearable
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setStatusFilter((value as string) || undefined);
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>
          </Grid>
        </div>
      </div>

      {error && <Message negative>{error}</Message>}
      {loading && <Loader active inline='centered' />}

      {!loading && !error && entries.length === 0 && (
        <Message info>No ingestion log entries found.</Message>
      )}

      {!loading && !error && entries.length > 0 && (
        <div className='card'>
          <div className='admin-companies-search-meta' style={{ marginBottom: '0.5rem' }}>
            Showing {(currentPage - 1) * PAGE_SIZE + 1} -{' '}
            {Math.min(currentPage * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + entries.length)} of {total} entries
            {hasFilters && (
              <>
                {' - '}
                <button type='button' className='admin-companies-clear-link' onClick={clearAllFilters}>
                  Clear filters
                </button>
              </>
            )}
          </div>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <STable striped>
              <STable.Header>
                <STable.Row>
                  <STable.HeaderCell onClick={handleToggleSortByDate} style={{ cursor: 'pointer' }}>
                    Date{sortIcon}
                  </STable.HeaderCell>
                  <STable.HeaderCell>Type</STable.HeaderCell>
                  <STable.HeaderCell>Status</STable.HeaderCell>
                  <STable.HeaderCell>User</STable.HeaderCell>
                  <STable.HeaderCell>Project</STable.HeaderCell>
                  <STable.HeaderCell>Result</STable.HeaderCell>
                </STable.Row>
              </STable.Header>
              <STable.Body>
                {entries.map((entry) => {
                  const isOpen = expandedId === entry.id;
                  const summary = resultSummary(entry);
                  const hasResult = entry.result != null;
                  return (
                    <React.Fragment key={entry.id}>
                      <STable.Row
                        onClick={() => setExpandedId(isOpen ? null : entry.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <STable.Cell style={{ whiteSpace: 'nowrap' }}>
                          <Icon name={isOpen ? 'caret down' : 'caret right'} />
                          {new Date(entry.createdAt).toLocaleString()}
                        </STable.Cell>
                        <STable.Cell>{entry.type}</STable.Cell>
                        <STable.Cell>
                          <span style={{ color: statusColor(entry.status), fontWeight: 600 }}>
                            {entry.status}
                          </span>
                        </STable.Cell>
                        <STable.Cell>
                          {entry.user?.email ?? entry.user?.name ?? (entry.userId != null ? `#${entry.userId}` : '')}
                        </STable.Cell>
                        <STable.Cell>
                          {entry.project?.id ? (
                            <Link
                              to={`/project/${entry.project.parentId ?? entry.project.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {entry.project.name || `#${entry.project.id}`}
                            </Link>
                          ) : (
                            entry.projectId != null ? `#${entry.projectId}` : ''
                          )}
                        </STable.Cell>
                        <STable.Cell style={{ minWidth: 280, maxWidth: 560 }}>
                          {hasResult ? (
                            <span>{summary.length > 120 ? `${summary.slice(0, 119)}…` : summary}</span>
                          ) : (
                            <span style={{ color: '#888' }}>—</span>
                          )}
                        </STable.Cell>
                      </STable.Row>
                      {isOpen && (
                        <STable.Row>
                          <STable.Cell colSpan={6} style={{ background: '#fafafa', padding: '1rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                  Uploaded document
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {entry.fileName || <span style={{ color: '#888', fontFamily: 'inherit' }}>(not recorded)</span>}
                                  </span>
                                  {entry.hasDocument && <DocumentDownloadLink apiEventId={entry.id} />}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                  Additional context
                                </div>
                                {entry.additionalContext ? (
                                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                                    {entry.additionalContext}
                                  </div>
                                ) : (
                                  <span style={{ color: '#888' }}>(none)</span>
                                )}
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                  Result
                                </div>
                                {hasResult ? (
                                  <pre
                                    style={{
                                      margin: 0,
                                      padding: '0.75rem',
                                      background: '#f7f7f7',
                                      border: '1px solid #ddd',
                                      borderRadius: 4,
                                      fontSize: '0.75rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      maxHeight: 480,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {JSON.stringify(entry.result, null, 2)}
                                  </pre>
                                ) : (
                                  <span style={{ color: '#888' }}>No result data.</span>
                                )}
                              </div>
                            </div>
                          </STable.Cell>
                        </STable.Row>
                      )}
                    </React.Fragment>
                  );
                })}
              </STable.Body>
            </STable>
          </div>
          <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
                totalPages={totalPages}
                siblingRange={1}
                boundaryRange={1}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: '←', icon: false }}
                nextItem={{ content: '→', icon: false }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
